import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Tag } from '@/types/database'

interface TagBadgeProps {
  tag: Tag | string
  onRemove?: () => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
  // For when tag is just a string, provide optional color
  color?: string
}

/**
 * TagBadge - Displays a tag with optional remove button
 *
 * Can accept either a full Tag object or just a tag name string.
 * When using a string, you can optionally provide a color.
 */
export function TagBadge({ tag, onRemove, size = 'md', className, color }: TagBadgeProps) {
  // Extract values based on whether tag is an object or string
  const displayName = typeof tag === 'string' ? `#${tag}` : tag.display_name
  const tagColor = typeof tag === 'string' ? (color || '#6b7280') : tag.color

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  }

  // Get text color (white for dark backgrounds, black for light)
  const getTextColor = (hexColor: string) => {
    // Convert hex to RGB
    const hex = hexColor.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }

  const textColor = getTextColor(tagColor)

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium transition-colors',
        sizeClasses[size],
        className
      )}
      style={{
        backgroundColor: tagColor,
        color: textColor,
      }}
    >
      {displayName}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="hover:opacity-70 transition-opacity"
          aria-label="Remove tag"
        >
          <X className={cn('shrink-0', size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
        </button>
      )}
    </span>
  )
}
